/**
 * Surface icon rendering, shared by the shell's tab strip and the chat's
 * marker buttons. Icon substitution speaks two forms: an @mui/icons-material
 * export name ("Mail") from the curated map below, or an http(s)/data: image
 * URL for a client's own artwork.
 *
 * The map is deliberately CURATED, not a dynamic lookup: it covers the whole
 * LV surface vocabulary (the learner-page fan-out's fourteen surfaces plus
 * shell chrome) and the handful of alternates operators plausibly pick in
 * lv_settings. A barrel/dynamic import of icons-material would pull ~2,000
 * modules into the bundle for zero learner-visible gain. An unknown name
 * falls back to the generic tab glyph — add the export here when a client
 * asks for one that isn't covered (one line, one redeploy), or hand them an
 * image URL, which needs no deploy at all.
 */
import { Box } from '@mui/material'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import InboxIcon from '@mui/icons-material/Inbox'
import NotificationsIcon from '@mui/icons-material/Notifications'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import SchoolIcon from '@mui/icons-material/School'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import TabIcon from '@mui/icons-material/Tab'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ForumIcon from '@mui/icons-material/Forum'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import LocalActivityIcon from '@mui/icons-material/LocalActivity'
import RouteIcon from '@mui/icons-material/Route'
import Diversity3Icon from '@mui/icons-material/Diversity3'
import BadgeIcon from '@mui/icons-material/Badge'
import CardMembershipIcon from '@mui/icons-material/CardMembership'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import GroupsIcon from '@mui/icons-material/Groups'
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount'
import FlipIcon from '@mui/icons-material/Flip'
import TimelineIcon from '@mui/icons-material/Timeline'
import DescriptionIcon from '@mui/icons-material/Description'
import FolderIcon from '@mui/icons-material/Folder'

const ICON_MAP: Record<string, typeof MailOutlineIcon> = {
  Mail: MailOutlineIcon,
  Inbox: InboxIcon,
  Notifications: NotificationsIcon,
  CalendarMonth: CalendarMonthIcon,
  MenuBook: MenuBookIcon,
  School: SchoolIcon,
  Home: HomeOutlinedIcon,
  // The learner-page fan-out vocabulary (plan §2 surface map):
  Assignment: AssignmentIcon, // transcript
  ReceiptLong: ReceiptLongIcon, // purchases
  WorkspacePremium: WorkspacePremiumIcon, // credentials
  AccountCircle: AccountCircleIcon, // profile
  Forum: ForumIcon, // forums
  ConfirmationNumber: ConfirmationNumberIcon, // codes
  EmojiEvents: EmojiEventsIcon, // badges
  LocalActivity: LocalActivityIcon, // passes
  Route: RouteIcon, // paths
  Diversity3: Diversity3Icon, // connections
  // Classic-parity alternates:
  Badge: BadgeIcon,
  CardMembership: CardMembershipIcon,
  ShoppingCart: ShoppingCartIcon,
  Groups: GroupsIcon,
  // Two-sided surface faces + chrome:
  SupervisorAccount: SupervisorAccountIcon, // leader face
  Flip: FlipIcon, // the flip affordance
  Timeline: TimelineIcon,
  Description: DescriptionIcon,
  Folder: FolderIcon, // nav folders (Explore-menu grouping)
}

export function SurfaceGlyph({ icon, size = 15 }: { icon: string; size?: number }) {
  if (/^(https?:|data:)/.test(icon)) {
    return <Box component="img" src={icon} alt="" sx={{ width: size, height: size, objectFit: 'contain' }} />
  }
  const Mui = ICON_MAP[icon]
  if (Mui) return <Mui sx={{ fontSize: size }} />
  return <TabIcon sx={{ fontSize: size }} />
}
